/**
 * Sprint 35 — Parser puro de pedidos llegados por WhatsApp.
 *
 * No usa IA externa (restricción operativa). Aplica heurísticas regex
 * sobre la plantilla canónica que el negocio le envía a sus clientes:
 *
 *   Hola, quiero pedir:
 *   📋 Mi pedido:
 *   - 2 alitas BBQ
 *   - 1 coca de 600
 *   📞 Mi teléfono: 9931234567
 *   📍 Mi dirección: Av Reforma 123, Col. Centro
 *   🗺️ Ubicación: https://maps.app.goo.gl/abc123
 *   📝 Referencias: portón verde junto al OXXO
 *
 * Para mensajes libres (cliente que no usó la plantilla) hace su mejor
 * esfuerzo. La UI muestra preview de lo detectado y la dueña ajusta.
 *
 * Función pura: sin side-effects, sin acceso a red, sin dependencias.
 */

export interface ItemExtraido {
  /** Cantidad detectada (1 si no aparece número). */
  cantidad: number;
  /** Descripción del producto tal cual (best-effort, sin matching contra catálogo). */
  descripcion: string;
}

export interface PedidoExtraido {
  nombre?: string;
  telefono?: string;
  direccion?: string;
  /** URL completo de Google Maps / app.goo.gl si lo encontró. */
  ubicacionUrl?: string;
  /** Coords extraídas del URL (cuando aplica). */
  latitud?: number;
  longitud?: number;
  referencias?: string;
  items: ItemExtraido[];
  /** Texto original recibido (para auditoría / corrección manual). */
  textoOriginal: string;
  /**
   * Sprint 98 — Las líneas que este intérprete **consumió como datos del cliente**.
   *
   * Existe para que el intérprete del menú (`leerPedidoDeChat`) no vuelva a preguntar por ellas.
   * Los dos leen el mismo mensaje sin saber el uno del otro, y el resultado se veía en pantalla:
   * el modal decía arriba *"leí la dirección: Jupiter 206, Fracc. Galaxia, Tabasco 2000, C.P.
   * 86035"* y abajo abría **cuatro** tarjetas preguntando qué producto del menú era cada pedazo
   * de esa misma dirección. El sistema contradiciéndose a sí mismo en la misma ventana.
   */
  lineasDeDatos: string[];
}

// ============================================================================
// Constantes regex
// ============================================================================

// Línea con item: opcional "- " o "* " o "• " al inicio, opcional cantidad
// con "x" o sin, luego descripción. Captura grupos: cantidad?, descripcion.
// Aceptamos cantidades con punto decimal o coma (ej: "1.5 kg", "0,5").
const ITEM_REGEX =
  /^[\s\-*•·]*(?:(\d+(?:[.,]\d+)?)\s*(?:x|×|de|de\s)?)?\s*(.+?)$/i;

// Palabras-cantidad textual (orden de búsqueda importa: "media" antes que "una")
const CANTIDAD_TEXTUAL: Array<{ regex: RegExp; valor: number }> = [
  { regex: /\bmedia\s+orden\b/i, valor: 0.5 },
  { regex: /\bmedio\s+/i, valor: 0.5 },
  { regex: /\bmedia\s+/i, valor: 0.5 },
  { regex: /\buna\s+/i, valor: 1 },
  { regex: /\bun\s+/i, valor: 1 },
  { regex: /\bdos\s+/i, valor: 2 },
  { regex: /\btres\s+/i, valor: 3 },
  { regex: /\bcuatro\s+/i, valor: 4 },
  { regex: /\bcinco\s+/i, valor: 5 },
];

const TELEFONO_REGEX = /(?:^|[^\d])((?:\+?52[\s-]?)?\d{2,3}[\s-]?\d{3,4}[\s-]?\d{4})(?:$|[^\d])/;

const URL_MAPS_REGEX =
  /(https?:\/\/(?:(?:www\.)?google\.com\/maps\S*|maps\.google\.com\S*|maps\.app\.goo\.gl\/\S+|goo\.gl\/maps\/\S+))/i;

const COORDS_EN_URL_REGEX =
  /(?:[?&@]q=|@|[?&]ll=|[?&]center=)(-?\d+\.\d+)[,%\s]+(-?\d+\.\d+)/;

// Palabras que sugieren "esta línea es dirección".
const HINT_DIRECCION =
  /\b(calle|av(?:e?nida)?|callej[oó]n|carretera|prolongaci[oó]n|colonia|col\.?|fracc(?:ionamiento)?|c\.?p\.?|frente\s+a|junto\s+a)\b/i;

// Palabras que sugieren "esta línea es referencia / instrucción de entrega".
const HINT_REFERENCIA =
  /\b(port[oó]n|color|casa\s+(?:de|color)|junto\s+al|frente\s+al|esquina|ref(?:erencia)?[:.]?)\b/i;

// Líneas que NO son items aunque pasen el regex item (stopwords).
const NO_ES_ITEM = [
  /^hola\b/i,
  /^buenas\b/i,
  /^buen[oa]s\s+(d[ií]as|tardes|noches)/i,
  /^quiero\s+pedir/i,
  /^quisiera\s+pedir/i,
  /^mi\s+(pedido|telefono|tel[eé]fono|direcci[oó]n|ubicaci[oó]n|referencia)/i,
  /^📋|^📞|^📍|^🗺|^📝|^👋/u,
  /^pedido[:.]?$/i,
  /^direcci[oó]n[:.]?$/i,
  /^tel[eé]fono[:.]?$/i,
  /^cliente[:.]?$/i,
  /^gracias\b/i,
  /^enviar\s+a/i,
];

const EMOJI_PREFIJO = /^[^\w\s]+\s*/u; // limpia emojis al inicio

// ============================================================================
// Función principal
// ============================================================================

export function parsePedidoWa(texto: string): PedidoExtraido {
  const original = texto ?? '';
  const lineas = original
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  // --- Detección de URL de maps + coords ---
  let ubicacionUrl: string | undefined;
  let latitud: number | undefined;
  let longitud: number | undefined;
  const matchUrl = original.match(URL_MAPS_REGEX);
  if (matchUrl) {
    ubicacionUrl = matchUrl[1];
    const matchCoords = ubicacionUrl.match(COORDS_EN_URL_REGEX);
    if (matchCoords) {
      const lat = parseFloat(matchCoords[1]);
      const lng = parseFloat(matchCoords[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng) &&
          Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
        latitud = lat;
        longitud = lng;
      }
    }
  }

  // --- Detección de teléfono ---
  let telefono: string | undefined;
  const matchTel = original.match(TELEFONO_REGEX);
  if (matchTel) {
    telefono = normalizarTelefono(matchTel[1]);
  }

  // --- Recorrido línea por línea ---
  let nombre: string | undefined;
  let direccion: string | undefined;
  let referencias: string | undefined;
  const items: ItemExtraido[] = [];
  /** Lo que se consumió como dato del cliente: no es pedido y no debe preguntarse como tal. */
  const lineasDeDatos: string[] = [];

  for (const rawLinea of lineas) {
    const linea = rawLinea.replace(EMOJI_PREFIJO, '').trim();
    if (!linea) continue;

    // Saltar URLs ya capturadas como ubicación.
    if (URL_MAPS_REGEX.test(linea)) {
      lineasDeDatos.push(linea);
      continue;
    }

    // Nombre explícito: "Soy X" / "Me llamo X" / "Nombre: X"
    /*
      Sprint 98 — Se admite el saludo delante.

      El regex exigía que la línea EMPEZARA con "soy". Un mensaje que abre con
      "Hola soy José Frías" —la forma normal de presentarse— caía en las stopwords de cortesía,
      el nombre se perdía, y encima ese texto acababa preguntándose como si fuera un producto
      del menú.
    */
    const matchNombre = linea.match(
      /^(?:(?:hola|buenas|buenos\s+d[ií]as|buenas\s+tardes|buenas\s+noches)[\s,.!¡]+)?(?:soy|me\s+llamo|nombre[:.]?)\s+(.+)$/i,
    );
    if (matchNombre && !nombre) {
      nombre = limpiarNombre(matchNombre[1]);
      lineasDeDatos.push(linea);
      continue;
    }

    // Línea de teléfono explícita: "Teléfono: ..." — ya capturado por regex global,
    // pero saltar para no procesar como item.
    if (/^(?:mi\s+)?(?:tel[eé]fono|celular|cel|whats(?:app)?)[:.]?/i.test(linea)) {
      lineasDeDatos.push(linea);
      continue;
    }

    // Línea de dirección explícita. El emoji ya fue limpiado arriba; admitimos
    // prefijo "Mi " opcional ("Mi dirección: ...").
    const matchDirExplicita = linea.match(/^(?:mi\s+)?(?:direcci[oó]n|domicilio|dir)[:.]?\s*(.*)$/i);
    if (matchDirExplicita) {
      const valor = matchDirExplicita[1]?.trim();
      if (valor && !direccion) direccion = valor;
      lineasDeDatos.push(linea);
      continue;
    }

    // Referencia explícita.
    const matchRefExplicita = linea.match(/^(?:mis?\s+)?(?:referencia[s]?|notas?|instrucciones?)[:.]?\s*(.*)$/i);
    if (matchRefExplicita) {
      const valor = matchRefExplicita[1]?.trim();
      if (valor && !referencias) referencias = valor;
      lineasDeDatos.push(linea);
      continue;
    }

    // Heurística por contenido cuando no hay etiqueta explícita.
    /**
     * Sprint 98 — La dirección puede venir en varios renglones, y casi siempre viene así.
     *
     * La gente escribe el domicilio como lo diría en voz alta:
     *
     *     Domicilio: Jupiter 206
     *     Fracc. Galaxia
     *     Tabasco 2000
     *     C.P. 86035
     *
     * Antes sólo se quedaba con el primero (`!direccion` cerraba la puerta) y los tres siguientes
     * se iban al intérprete del menú, que abría una tarjeta *"no encontré esto en tu menú"* por
     * cada uno. Ahora se anexan: el repartidor recibe el domicilio completo y nadie tiene que
     * descartar tres veces su propio dato.
     *
     * Sólo se anexa lo que **parece domicilio** (calle, colonia, fraccionamiento, C.P.) y no es un
     * renglón de pedido: un "2 refrescos" nunca acaba dentro de la dirección.
     */
    if (HINT_DIRECCION.test(linea) && !esLineaItem(linea)) {
      direccion = direccion ? `${direccion}, ${linea}` : linea;
      lineasDeDatos.push(linea);
      continue;
    }
    if (!referencias && HINT_REFERENCIA.test(linea) && !esLineaItem(linea)) {
      referencias = linea;
      lineasDeDatos.push(linea);
      continue;
    }

    // Stopwords: no es item.
    if (NO_ES_ITEM.some((re) => re.test(linea))) continue;

    // Si pasamos todos los filtros, intentamos extraer item.
    const item = extraerItem(linea);
    if (item) items.push(item);
    else if (!nombre && esPosibleNombre(linea)) {
      nombre = limpiarNombre(linea);
    }
  }

  return {
    nombre,
    telefono,
    direccion,
    ubicacionUrl,
    latitud,
    longitud,
    referencias,
    items,
    textoOriginal: original,
    lineasDeDatos,
  };
}

// ============================================================================
// Helpers internos
// ============================================================================

function extraerItem(linea: string): ItemExtraido | null {
  // Primero busca cantidad textual (media orden, dos, etc).
  let cantidad: number | null = null;
  let descripcion = linea;
  for (const ct of CANTIDAD_TEXTUAL) {
    if (ct.regex.test(linea)) {
      cantidad = ct.valor;
      descripcion = linea.replace(ct.regex, '').trim();
      break;
    }
  }

  if (cantidad === null) {
    const match = linea.match(ITEM_REGEX);
    if (!match) return null;
    const numStr = match[1];
    descripcion = (match[2] ?? '').trim();
    if (numStr) {
      const num = parseFloat(numStr.replace(',', '.'));
      cantidad = Number.isFinite(num) && num > 0 ? num : 1;
    } else {
      cantidad = 1;
    }
  }

  // Limpiar prefijos como "x ", "de "
  descripcion = descripcion.replace(/^(?:x|×|de)\s+/i, '').trim();
  // Limpiar trailing "por favor", "porfa", "porfis"
  descripcion = descripcion.replace(/\b(?:por\s+favor|porfa[a-z]*)\b\.?$/i, '').trim();

  if (descripcion.length < 2) return null;
  return { cantidad, descripcion };
}

function esLineaItem(linea: string): boolean {
  // Una línea es "item" si empieza con bullet o número, y NO contiene hints
  // fuertes de dirección/referencia.
  return /^[\s\-*•·]*\d/.test(linea);
}

function esPosibleNombre(linea: string): boolean {
  // Heurística débil: ≤ 4 palabras, sin dígitos, primera letra mayúscula.
  if (/\d/.test(linea)) return false;
  const palabras = linea.split(/\s+/);
  if (palabras.length === 0 || palabras.length > 4) return false;
  return /^[A-ZÁÉÍÓÚÑ]/.test(palabras[0]);
}

function limpiarNombre(s: string): string {
  return s
    .replace(/[.,;]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizarTelefono(s: string): string {
  // Quita espacios, guiones, paréntesis, signos.
  return s.replace(/[^\d+]/g, '');
}

// ============================================================================
// Builder de plantilla wa.me (Sprint 35)
// ============================================================================

/**
 * Construye el link wa.me que la dueña le envía a sus clientes.
 * Sanitiza el teléfono (solo dígitos, prepend 52 si parece MX sin código
 * de país).
 *
 * @param telefonoNegocio teléfono donde llegarán los pedidos (lo de la dueña)
 * @param plantilla texto que aparece pre-llenado al cliente
 */
export function construirLinkWaMe(telefonoNegocio: string, plantilla: string): string {
  let tel = (telefonoNegocio ?? '').replace(/\D/g, '');
  if (tel.length === 10) tel = `52${tel}`; // asumimos MX
  const textoEncoded = encodeURIComponent(plantilla);
  return `https://wa.me/${tel}?text=${textoEncoded}`;
}

/** Plantilla canónica en es-MX. La dueña puede editarla en Settings. */
export const PLANTILLA_CANONICA_ES_MX = `Hola, quiero pedir:

📋 Mi pedido:
- (escribe cantidad y producto, ej: 2 alitas BBQ)
-

📞 Mi teléfono:

📍 Mi dirección:

🗺️ Ubicación (opcional, comparte tu ubicación):

📝 Referencias (color de casa, junto a, etc.):
`;
